/**
 * SystemStatusPanel — left column, top panel.
 *
 * Truth: reflects real connection + agent state from the session hook.
 *   "Qoryn Online" appears once the WebRTC link is stable AND the agent is
 *   in a live conversational state; otherwise honestly shows Offline.
 * "Neural Mesh Stable" is a poetic label for the realtime link — same
 * boolean as connection === 'connected'.
 * The 100% ring visualizes readiness, not a fabricated production metric.
 */

import { PanelShell } from "./PanelShell";

function Ring({ value = 1 }) {
    const size = 68;
    const r = 26;
    const c = 2 * Math.PI * r;
    const filled = c * value;
    return (
        <svg width={size} height={size} viewBox="0 0 68 68" aria-hidden="true">
            <circle cx="34" cy="34" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
            <circle
                cx="34"
                cy="34"
                r={r}
                fill="none"
                stroke="rgba(0,229,255,0.9)"
                strokeWidth="3"
                strokeDasharray={`${filled} ${c}`}
                strokeLinecap="round"
                transform="rotate(-90 34 34)"
                style={{ transition: "stroke-dasharray 400ms ease" }}
            />
            <text
                x="34"
                y="38"
                textAnchor="middle"
                fill="rgba(243,244,246,0.95)"
                fontSize="12"
                fontFamily="'JetBrains Mono', monospace"
            >
                {Math.round(value * 100)}%
            </text>
        </svg>
    );
}

export function SystemStatusPanel({ agentState, connectionState, backendReady }) {
    const online =
        backendReady &&
        connectionState !== "closed" &&
        connectionState !== "failed" &&
        agentState !== "idle" &&
        agentState !== "error";

    const meshStable = connectionState === "connected";
    const readiness = backendReady
        ? online
            ? 1
            : 0.65
        : 0.2;

    return (
        <PanelShell title="System Status" testId="panel-system-status">
            <div className="flex items-center justify-between">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2" data-testid="sys-qoryn-status">
                        <span
                            className={`h-2 w-2 rounded-full ${
                                online
                                    ? "bg-qoryn-mint"
                                    : "bg-qoryn-textMuted/60"
                            }`}
                            style={{
                                boxShadow: online ? "0 0 8px rgba(94,247,198,0.7)" : "none",
                            }}
                        />
                        <span className="font-sans text-[13px] text-qoryn-text">
                            Qoryn {online ? "Online" : "Standby"}
                        </span>
                    </div>
                    <div className="flex items-center gap-2" data-testid="sys-mesh-status">
                        <span
                            className={`h-2 w-2 rounded-full ${
                                meshStable
                                    ? "bg-qoryn-cyan"
                                    : "bg-qoryn-textMuted/60"
                            }`}
                            style={{
                                boxShadow: meshStable ? "0 0 8px rgba(0,229,255,0.7)" : "none",
                            }}
                        />
                        <span className="font-sans text-[13px] text-qoryn-textDim">
                            Neural Mesh {meshStable ? "Stable" : "Idle"}
                        </span>
                    </div>
                    <div className="pt-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                        {backendReady ? "READINESS" : "AWAITING BACKEND"}
                    </div>
                </div>
                <Ring value={readiness} />
            </div>
        </PanelShell>
    );
}

export default SystemStatusPanel;
